import React, { useRef, useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import Hls from 'hls.js';
import DetectRTC, { isRTPSenderReplaceTracksSupported } from 'detectrtc';
import { Box, makeStyles, Typography } from '@material-ui/core';

import SaasLogger from '../../services/SaasLogger';
import GroupShowStore from '../../store/GroupShowStore';

import WebRTCPlayer from './webrtc';
import reducer, { initialState, PLAYBACK_STATE, ACTION_TYPE } from './reducer';


const PROVIDER_TYPE = {
  STREAMING_SAAS: 'STREAMING_SAAS',
  WOWZA: 'WOWZA',
  CAM4_STREAMING_SAAS: 'CAM4_STREAMING_SAAS',
};

const PROTOCOL = {
  HLS: 'HLS',
  WEBRTC: 'WebRTC',
};

const useStyles = makeStyles(theme => ({
  flagIcon: {
    backgroundColor: '#FF0000',
    position: 'absolute',
    top: 30,
    right: 10,
    borderRadius: '10px',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2px 10px',
    '& > p': {
      fontSize: '12px',
      fontWeight: 700,
    },
    [theme.breakpoints.down('sm')]: {
      top: 90,
      right: 15
    }
  },
}));

function StreamViewPlayer(props) {
  const [state, setState] = useReducer(reducer, initialState);
  const classes = useStyles();
  const {
    videoId,
    controls,
    videoClass,
    loadingOverlay,
    autoplay,
    onHLSError,
    streamData,
    user,
    anchorUsername,
    gsHost,
    isInGroupShow,
    hasStreamViews,
    toggleStreamLoading,
    isTulumRealityShow,
    onAllowStreamChangeOnError,
    onLiveSessionEnd,
  } = props;

  const videoRef = useRef();
  const player = useRef();
  const retryCount = useRef(0);

  useEffect(() => {
    if (player.current) {
      stopHLS(true);
    }
  }, [streamData]);

  useEffect(
    () => {
      if ((autoplay && hasStreamViews) || isInGroupShow) {
        playStream();
      }

      if (player.current && !autoplay) {
        stopStream();
      }

      return function () {
        stopOngoingStream();
      }
    },
    [autoplay, hasStreamViews, isInGroupShow]
  );

  const stopOngoingStream = () => {
    if (streamData.provider === PROVIDER_TYPE.WOWZA && player.current) {
      stopHLS();
    }
      
    if ((streamData.provider === PROVIDER_TYPE.STREAMING_SAAS ||
      streamData.provider === PROVIDER_TYPE.CAM4_STREAMING_SAAS) && player.current) {
      stopWebRTC();
    }
  }

  useEffect(() => {
    if (gsHost && isInGroupShow) {
      GroupShowStore.setHostVideoRef(videoRef);
    }

    if (!gsHost && isInGroupShow) {
      GroupShowStore.setParticipantVideoRef(videoRef);
    }
  }, []);

  const dispatch = (type, payload) => setState({ type, payload });

  const playStream = () => {

    if (streamData.provider === PROVIDER_TYPE.WOWZA) {
      const hls = streamData.streamViews[0];
      if (hls) {
        startHLS(hls);
      }
    }

    if (streamData.provider === PROVIDER_TYPE.STREAMING_SAAS ||
      streamData.provider === PROVIDER_TYPE.CAM4_STREAMING_SAAS) {
      DetectRTC.load(() => {
        if (DetectRTC.isWebRTCSupported) {
          const webrtc = streamData.streamViews.find(
            (item) => item.protocol === PROTOCOL.WEBRTC
          );
          if (webrtc) {
            startWebRTC(webrtc);
          }
        } else {
          const hls = streamData.streamViews.find(
            (item) => item.protocol === PROTOCOL.HLS
          );
          if (hls) {
            startHLS(hls);
          }
        }
      });
    }
  };

  useEffect(() => {
    retryCount.current = state.retryCount;
  }, [state.retryCount]);


  const stopStream = () => {
    state.activeProtocol === PROTOCOL.HLS ? stopHLS() : stopWebRTC();
  };

  const onStarted = () => {
    dispatch(ACTION_TYPE.SET_PLAYBACK_STATE, PLAYBACK_STATE.PLAYING);
  };

  const onStopped = () => {
    dispatch(ACTION_TYPE.SET_PLAYBACK_STATE, PLAYBACK_STATE.STOPPED);
  };

  const onStoppedWebRTC = () => {
    onStopped();
    onPlayError();
  };

  const onPlayError = () => {
    if (retryCount.current < 2) {
      retryCount.current++;

      // REMOVE THIS AFTER QA TESTING
      SaasLogger.log('RETRY');

      const webrtc = streamData.streamViews.find(
        (item) => item.protocol === PROTOCOL.WEBRTC
      );
      startWebRTC(webrtc);
    } else {
      const hls = streamData.streamViews.find(
        (item) => item.protocol === PROTOCOL.HLS
      );
      startHLS(hls);
    }
  };

  const startHLS = (hls) => {
    dispatch(ACTION_TYPE.SET_PLAYBACK_STATE, PLAYBACK_STATE.STARTING);
    dispatch(ACTION_TYPE.SET_ACTIVE_PROTOCOL, PROTOCOL.HLS);

    // REMOVE THIS AFTER QA TESTING
    SaasLogger.log('START HLS');

    if (hls && hls.url && videoRef.current) {
      const url =
        streamData.provider === PROVIDER_TYPE.WOWZA ? hls.url : `${hls.url}?id=${hls.accessToken}`;

      if (Hls.isSupported()) {
        videoRef.current.muted = state.muted;
        player.current = new Hls();
        player.current.loadSource(url);
        player.current.attachMedia(videoRef.current);
        player.current.on(Hls.Events.ERROR, (_, error) => {
          if (isTulumRealityShow) {
            if (error.fatal) {
              switch (error.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  player.current.startLoad();
                  onAllowStreamChangeOnError();
                  break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                  player.current.recoverMediaError();
                  onAllowStreamChangeOnError();
                  break;
                default:
                  player.current.destroy();
                  onHLSError(error);
                  break;
              }
            }
          } else {
            onHLSError(error);
          }
        });
        player.current.on(Hls.Events.MANIFEST_PARSED, () => {
          videoRef.current.play().then(() => {
            if (toggleStreamLoading) {
              toggleStreamLoading(false);
            }
            dispatch(ACTION_TYPE.SET_PLAYBACK_STATE, PLAYBACK_STATE.PLAYING);
          }).
            catch(() => {
              onStopped();
            });
        });
      } else if (
        videoRef.current.canPlayType('application/vnd.apple.mpegurl')
      ) {
        videoRef.current.muted = state.muted;
        videoRef.current.src = url;
        videoRef.current.addEventListener('loadedmetadata', () => {
          dispatch(ACTION_TYPE.SET_PLAYBACK_STATE, PLAYBACK_STATE.PLAYING);
          videoRef.current.play();
        });
      }
    }
  };

  const stopHLS = (shouldLoadNextCamera) => {
    videoRef.current.pause();
    videoRef.current.src = '';

    if (player.current && typeof(player.current.stopLoad) === 'function') {
      player.current.stopLoad();
      if (typeof(onLiveSessionEnd) === 'function') {
        onLiveSessionEnd();
      }
    }
    player.current = null;

    if (shouldLoadNextCamera) {
      playStream();
    }
  };

  const startWebRTC = (webrtc) => {
    dispatch(ACTION_TYPE.SET_PLAYBACK_STATE, PLAYBACK_STATE.STARTING);
    dispatch(ACTION_TYPE.SET_ACTIVE_PROTOCOL, PROTOCOL.WEBRTC);

    // REMOVE THIS AFTER QA TESTING
    SaasLogger.log('START WEBRTC');

    // UNCOMMENT THIS AFTER QA TESTING
    let url;
    if (streamData.provider === PROVIDER_TYPE.CAM4_STREAMING_SAAS) {
      url = webrtc.url;
    } else {
      url = `${webrtc.url}?id=${webrtc.accessToken}`;
    }
    if (videoRef.current) {
      videoRef.current.muted = state.muted;
    }
    const analyticsInfo = {
      userName: user.authenticated ? user.user.username : user.authenticated,
      anchorUsername,
      streamData,
      userAgent: navigator.userAgent,
    };
    SaasLogger.setMeta(analyticsInfo);

    if (videoRef.current) {
      player.current = new WebRTCPlayer(
        url,
        videoRef.current,
        onStarted,
        onStoppedWebRTC,
        onPlayError,
        analyticsInfo,
      );
      player.current.start();
    }

  };

  const stopWebRTC = () => {
    if (player.current && typeof(player.current.stop === 'function')) {
      player.current.stop();
      if (typeof(onLiveSessionEnd) === 'function') {
        onLiveSessionEnd();
      }
    }
  };

  const onMuteChange = () => {
    dispatch(ACTION_TYPE.SET_MUTE);
    videoRef.current.muted = !state.muted;
  };

  const onVolumeChange = (volume) => {
    dispatch(ACTION_TYPE.SET_VOLUME, volume);
    videoRef.current.volume = volume;
  };

  return (
    <React.Fragment>
      <video
        ref={videoRef}
        autoPlay={true}
        className={videoClass}
        id={videoId}
        playsInline={true}
      />
      {streamData.isFeatured && (
        <Box className={classes.flagIcon}>
          <Typography>FEATURED</Typography>
        </Box>
      )}
      {loadingOverlay &&
        loadingOverlay(state.playbackState === PLAYBACK_STATE.STARTING)}
      {controls &&
        controls({
          muted: state.muted,
          volume: state.volume,
          showPlay: state.playbackState === PLAYBACK_STATE.STOPPED,
          disableMute: state.playbackState === PLAYBACK_STATE.STARTING,
          startHLS,
          stopStream,
          playStream,
          onMuteChange,
          onVolumeChange,
        })}
    </React.Fragment>
  );
}

StreamViewPlayer.propTypes = {
  autoPlay: PropTypes.bool,
  videoId: PropTypes.string,
  controls: PropTypes.func,
  videoClass: PropTypes.string,
  loadingOverlay: PropTypes.func,
  autoplay: PropTypes.bool,
  onHLSError: PropTypes.func,
  user: PropTypes.object,
  streamData: PropTypes.shape({
    provider: PropTypes.oneOf([
      PROVIDER_TYPE.STREAMING_SAAS,
      PROVIDER_TYPE.WOWZA,
      PROVIDER_TYPE.CAM4_STREAMING_SAAS,
    ]),
  }),
};

StreamViewPlayer.defaultProps = { autoplay: true };

export default StreamViewPlayer;
